import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Star } from "lucide-react";
import { Logo } from "@/components/Logo";
import { SITE, NAV_LINKS } from "@/data/site";
import { SERVICES } from "@/data/services";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-[#1a1a22] bg-[#08080b]" data-testid="site-footer">
      <div className="container-kx grid gap-10 py-14 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <Logo variant="stacked" />
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-neutral-400">
            {SITE.tagline}. Licensed {SITE.license} serving {SITE.serviceZip} and {SITE.county}.
          </p>
          <div className="mt-5 space-y-2.5 text-sm">
            <a href={SITE.phonePrimary.tel} data-testid="footer-call-primary" className="flex items-center gap-2.5 text-neutral-300 hover:text-amber-400">
              <Phone className="h-4 w-4 text-amber-400" /> {SITE.phonePrimary.display}
            </a>
            <a href={SITE.phoneSecondary.tel} data-testid="footer-call-secondary" className="flex items-center gap-2.5 text-neutral-300 hover:text-amber-400">
              <Phone className="h-4 w-4 text-amber-400" /> {SITE.phoneSecondary.display}
            </a>
            <a href={SITE.emailHref} data-testid="footer-email" className="flex items-center gap-2.5 text-neutral-300 hover:text-amber-400">
              <Mail className="h-4 w-4 text-amber-400" /> {SITE.email}
            </a>
            <p className="flex items-start gap-2.5 text-neutral-400">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" /> {SITE.serviceAreaShort}
            </p>
            <div className="flex items-start gap-2.5 text-neutral-400">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
              <span>
                {SITE.hours.display.map((h) => (
                  <span key={h.days} className="block">
                    {h.days}: <span className="text-neutral-300">{h.time}</span>
                  </span>
                ))}
              </span>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="footer-social">
            <a
              href={SITE.googleReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-review-link"
              className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-4 py-2 text-sm font-semibold text-amber-300 transition-colors hover:bg-amber-400/20"
            >
              <Star className="h-4 w-4" /> Review us on Google
            </a>
            <a
              href={SITE.googleUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-google-link"
              aria-label="Kensmarc Electric on Google"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#26262f] transition-colors hover:border-amber-400/60"
            >
              <GoogleIcon className="h-4 w-4" />
            </a>
            <a
              href={SITE.tiktokUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-tiktok-link"
              aria-label="Kensmarc Electric on TikTok"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#26262f] text-white transition-colors hover:border-amber-400/60"
            >
              <TikTokIcon className="h-4 w-4" />
            </a>
            <a
              href={SITE.yelpUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-yelp-link"
              aria-label="Kensmarc Electric on Yelp"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#26262f] text-white transition-colors hover:border-amber-400/60"
            >
              <YelpIcon className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div>
          <h3 className="font-display text-sm font-bold uppercase tracking-widest text-white">Company</h3>
          <ul className="mt-4 space-y-2.5 text-sm">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-neutral-400 transition-colors hover:text-amber-400">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-display text-sm font-bold uppercase tracking-widest text-white">Services</h3>
          <ul className="mt-4 space-y-2.5 text-sm">
            {SERVICES.slice(0, 7).map((s) => (
              <li key={s.slug}>
                <Link to={`/services/${s.slug}`} className="text-neutral-400 transition-colors hover:text-amber-400">
                  {s.name}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/cctv-security" className="text-neutral-400 transition-colors hover:text-amber-400">
                CCTV &amp; Security
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <h3 className="font-display text-sm font-bold uppercase tracking-widest text-white">Get Started</h3>
          <p className="mt-4 text-sm text-neutral-400">
            Tell us about your project and we'll follow up with next steps.
          </p>
          <Link
            to="/request-a-quote"
            data-testid="footer-quote-btn"
            className="mt-4 inline-flex rounded-full bg-amber-400 px-5 py-2.5 text-sm font-bold uppercase tracking-wide text-[#0A0A0C] transition-all hover:bg-amber-300"
          >
            Request a Quote
          </Link>
          <Link to="/contact" className="mt-3 block text-sm font-medium text-neutral-300 hover:text-amber-400">
            Contact Kensmarc Electric →
          </Link>
        </div>
      </div>

      <div className="border-t border-[#161620]">
        <div className="container-kx flex flex-col items-center justify-between gap-3 py-5 text-xs text-neutral-500 sm:flex-row">
          <p>© {year} {SITE.name}. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <Link to="/privacy-policy" className="hover:text-neutral-300">Privacy Policy</Link>
            <Link to="/service-area" className="hover:text-neutral-300">Service Area</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function GoogleIcon({ className }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.3-5.6l-6.6-5.6C29.5 34.6 26.9 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.6 5.1C9.6 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.6l6.6 5.6C41.4 36 44 30.5 44 24c0-1.3-.1-2.3-.4-3.5z" />
    </svg>
  );
}

function TikTokIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M16.5 3c.4 2.3 1.72 3.94 4 4.2v2.74c-1.42.14-2.66-.32-4.02-1.05v6.16c0 3.62-2.86 5.95-5.9 5.95-2.86 0-5.08-2.05-5.08-4.9 0-3.02 2.3-5.02 5.3-4.9.3.01.58.04.9.1v2.82a3.3 3.3 0 0 0-.9-.16c-1.28 0-2.32 1-2.32 2.28 0 1.3 1.02 2.3 2.3 2.3 1.4 0 2.6-1.08 2.6-2.9V3h3.12z" />
    </svg>
  );
}

function YelpIcon({ className }) {
  return (
    <svg viewBox="0 0 384 512" className={className} fill="#D32323" aria-hidden="true">
      <path d="M42.9 240.3l99.6 48.6c19.2 9.4 16.1 37.5-4.5 42.6L30.5 358c-14.7 3.6-28.7-7.6-28.5-22.8l1.5-90.6c.3-21.1 24.8-33.6 43.9-24.3zm44.6-88.5l77.1 63.6c15.4 12.7 40.1-3.8 33.1-25.3L152.6 27.9c-4.5-13.9-19.5-21.3-33.3-16.4L64.4 30.9c-14.6 5.2-19.5 23-9.8 35.4l32.9 85.5zM188 302.5c-8.3 3.1-13.3 11.7-11.9 20.5l16 96.3c3.3 20.2 30.3 24.1 39.1 5.7l45.4-93.6c8.3-17.1-8.5-35.9-26.5-29.7L188 302.5zm144.2-15.7L242 349.2c-17.4 12.1-4.5 39.5 16.3 34.6l111.2-25.8c14.7-3.4 22.4-19.9 15.4-33.3l-42-80.5c-9.8-18.7-38.5-11.5-36.1 8.9l25.4 21.9c-.1-.1-.1-.1 0 0zM217.4 174.9L242 30.5C245.3 10.3 218.3-3.6 209.5 14.8L164 108.4c-8.3 17.1 8.5 35.9 26.5 29.7l26.9-63.2z" />
    </svg>
  );
}
